/**
 * Registro da coleta da amostra.
 *
 * A coleta é o começo do ciclo: só data e atendimento. O laudo do laboratório
 * vem dias depois, por outra tela e outra permissão.
 *
 * A lista de atendimentos é filtrada pelo tipo `Toxicológico` — registrar
 * coleta num atendimento de ASO comum produziria um S-2221 sem sentido.
 *
 * Ela vem por `useListaPermitida` e não por chamada direta. A porta da tela
 * cobra `toxicological_exam.read`, e buscar atendimento cobra
 * `appointment.read`: quem tem a primeira e não a segunda abriria o diálogo e
 * encontraria o campo mudo, sem saber que o que faltou foi acesso e não
 * cadastro. O gancho pergunta antes de pedir e separa recusa de falha.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import toxicologicalExamService from '../../../services/toxicologicalExamService';
import appointmentService from '../../../services/appointmentService';
import { AppointmentExamType } from '../../../types/appointment';
import { useListaPermitida } from '../../../authorization/useListaPermitida';

interface OpcaoDeAtendimento {
  id: number;
  rotulo: string;
}

interface Props {
  aberto: boolean;
  onFechar: () => void;
  onSalvo: () => void;
}

function hojeISO(): string {
  return new Date().toISOString().slice(0, 10);
}

export const RegistrarColetaDialog: React.FC<Props> = ({ aberto, onFechar, onSalvo }) => {
  const [atendimento, setAtendimento] = useState<OpcaoDeAtendimento | null>(null);
  const [collectionDate, setCollectionDate] = useState(hojeISO());
  const [notes, setNotes] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const carregarAtendimentos = useCallback(async (): Promise<readonly OpcaoDeAtendimento[]> => {
    const resposta = await appointmentService.getAppointments({
      limit: 100,
      examType: AppointmentExamType.TOXICOLOGICO,
    });
    return resposta.appointments.map((a) => ({
      id: a.id,
      rotulo: `#${a.id} — ${a.employeeName} — ${a.selectedDate}`,
    }));
  }, []);

  // `habilitado` só enquanto aberto: diálogo fechado não busca nada.
  const atendimentos = useListaPermitida<OpcaoDeAtendimento>(
    'appointment.read',
    'Atendimentos',
    carregarAtendimentos,
    { habilitado: aberto }
  );

  // Reabrir é recomeçar: o formulário volta ao estado inicial a cada abertura.
  useEffect(() => {
    if (!aberto) return;
    setErro(null);
    setAtendimento(null);
    setCollectionDate(hojeISO());
    setNotes('');
  }, [aberto]);

  const salvar = useCallback(async () => {
    if (!atendimento) {
      setErro('Escolha o atendimento da coleta');
      return;
    }
    if (!collectionDate) {
      setErro('Informe a data da coleta');
      return;
    }
    setSalvando(true);
    setErro(null);
    try {
      await toxicologicalExamService.registrarColeta({
        appointmentId: atendimento.id,
        collectionDate,
        notes: notes || null,
      });
      onSalvo();
      onFechar();
    } catch (err: unknown) {
      setErro(err instanceof Error ? err.message : 'Não foi possível registrar a coleta');
    } finally {
      setSalvando(false);
    }
  }, [atendimento, collectionDate, notes, onSalvo, onFechar]);

  return (
    <Dialog open={aberto} onClose={onFechar} maxWidth="sm" fullWidth>
      <DialogTitle>Registrar coleta de exame toxicológico</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {erro && <Alert severity="error">{erro}</Alert>}

          {atendimentos.erro && <Alert severity="error">{atendimentos.erro}</Alert>}

          {/* Recusa de acesso e ausência de cadastro pedem reações opostas de
              quem lê: uma se resolve com o administrador, a outra agendando. */}
          {atendimentos.bloqueio && <Alert severity="warning">{atendimentos.bloqueio}</Alert>}

          {atendimentos.resolvida && atendimentos.permitido && atendimentos.itens.length === 0 && (
            <Alert severity="info">
              Nenhum atendimento do tipo Toxicológico encontrado. Agende o exame primeiro, em
              Agendamento de Exames, escolhendo o tipo Toxicológico.
            </Alert>
          )}

          <Autocomplete<OpcaoDeAtendimento>
            options={[...atendimentos.itens]}
            value={atendimento}
            loading={atendimentos.carregando}
            disabled={!atendimentos.permitido}
            onChange={(_e, valor) => setAtendimento(valor)}
            getOptionLabel={(o) => o.rotulo}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Atendimento"
                placeholder="Busque pelo trabalhador"
                helperText={atendimentos.bloqueio}
              />
            )}
          />

          <TextField
            label="Data da coleta"
            type="date"
            value={collectionDate}
            onChange={(e) => setCollectionDate(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
            helperText="O prazo de 30 meses conta desta data, não da entrega do laudo"
          />

          <TextField
            label="Observações"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            multiline
            rows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onFechar} disabled={salvando}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={() => void salvar()} disabled={salvando}>
          {salvando ? 'Salvando…' : 'Registrar coleta'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RegistrarColetaDialog;
